"use client";

import Link from "next/link";
import { useSession } from "next-auth/react";
import { useEffect, useMemo, useState } from "react";

import { UserPlan, Plan, Category } from "@/types";

import NavServicesButton from "@/app/components/Navigation/components/NavServicesButton";
import NavProfileButton from "@/app/components/Navigation/components/NavProfileButton";
import NavNotificationButton from "@/app/components/Navigation/components/NavNotificationButton";
import NavMobileMenuButton from "@/app/components/Navigation/components/NavMobileMenuButton";
import Logo from "@/components/Logo";

import NavBasketButton from "@/app/components/Navigation/components/NavBasketButton";

type NavigationProps = {
	initialNavCategories?: Category[];
};

export default function Navigation({ initialNavCategories = [] }: NavigationProps) {
	// CRITICAL: Use useMemo to stabilize session reference and prevent unnecessary re-renders
	// This prevents the component from re-rendering when session object reference changes
	// but the actual session data hasn't changed
	const sessionResult = useSession();
	const session = useMemo(() => sessionResult.data, [sessionResult.data?.user?.id, sessionResult.data?.idToken]);
	const sessionStatus = sessionResult.status;
	const hasServerCategories = initialNavCategories.length > 0;
	const [categories, setCategories] = useState<{ docs: Category[] }>(() =>
		hasServerCategories ? { docs: initialNavCategories } : { docs: [] },
	);
	const [plan, setPlan] = useState<Plan | null>(null);
	const [isLoading, setIsLoading] = useState(!hasServerCategories);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);

		if (hasServerCategories) {
			return;
		}

		import("@/lib/api/categories")
			.then((module) => module.getCategories(1, 20, { sortBy: "order:asc" }))
			.then((data) => {
				setCategories(data);
				setIsLoading(false);
			})
			.catch(() => {
				setCategories({ docs: [] });
				setIsLoading(false);
			});
	}, [hasServerCategories]);

	// CRITICAL: Memoize session user ID and plan ID to prevent unnecessary plan fetches
	// This ensures the effect only runs when the actual plan changes, not when session object reference changes
	const sessionUserId = useMemo(() => session?.user?.id, [session?.user?.id]);
	const sessionPlanId = useMemo(() => session?.user?.plan?.planID, [session?.user?.plan?.planID]);
	const sessionPlanDuration = useMemo(() => session?.user?.plan?.duration, [session?.user?.plan?.duration]);
	const authenticatedSession =
		mounted && sessionStatus === "authenticated" && session?.user?.id ? session : null;
	
	useEffect(() => {
		// Fetch user plan if session exists and has a plan
		if (session && sessionPlanDuration !== UserPlan.notSelected && sessionPlanId) {
			// Dynamically import getPlan and fetch plan data
			import("@/lib/api/plan")
				.then((module) => {
					return module.getPlan(sessionPlanId);
				})
				.then((planData) => {
					setPlan(planData);
				})
				.catch((error) => {
					// Gracefully handle plan fetch errors
					setPlan(null);
				});
		} else {
			setPlan(null);
		}
	}, [session, sessionPlanId, sessionPlanDuration]);

	return (
		<>
			<Link href="/" className="inline-flex items-center">
				<Logo />
			</Link>

			<div className="ml-8 hidden items-center gap-6 text-sm2 font-semibold md:inline-flex">
				<NavServicesButton categories={categories.docs} className="hidden lg:block" />

				<Link href="/">Home</Link>
				<Link href="/subscription/monthly">Plans</Link>
				<Link href="/hosting/monthly">Hosting</Link>
			</div>

			<div className="inline-flex items-center justify-end gap-0.5 md:flex-1 md:gap-1">
				<NavBasketButton />
				{/* Only render session-dependent components after hydration to prevent hydration mismatches */}
				{authenticatedSession && <NavNotificationButton />}
				<div className="ml-0.5 md:ml-1 md:hidden">
					<NavMobileMenuButton session={authenticatedSession} plan={plan} />
				</div>
			</div>

			<div className="hidden items-center md:ml-1 md:inline-flex">
				<NavProfileButton session={authenticatedSession} />
			</div>
		</>
	);
}
